import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { AssignAssigneesDialog } from "@/components/account/AssignAssigneesDialog";
import {
  AdminAlert,
  AdminEmptyState,
  AdminList,
  AdminListRow,
  AdminPageHeader,
  AdminPill,
} from "@/components/shell/AdminPage";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { rememberAccountId, useAccountWorkspace } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type Membership = { user_id: number; role: "owner" | "admin" | "member" };

type Authorization = {
  id: number;
  grantor_account_id: number;
  grantee_account_id: number;
  grantor_account_name?: string | null;
  grantee_account_name?: string | null;
  authorization_type?: "service_provider";
  permission: "read_only" | "maintain";
  status: "active" | "revoked";
};

const authorizationSchema = z.object({
  grantee_account_id: z.string().trim().regex(/^[1-9]\d*$/, "请输入有效的被授权账户 ID"),
  permission: z.enum(["read_only", "maintain"]),
});

type AuthorizationFormValues = z.infer<typeof authorizationSchema>;

const permissionLabels: Record<Authorization["permission"], string> = {
  read_only: "只读",
  maintain: "代管",
};

type AuthTab = "outbound" | "received";

function accountTitle(name: string | null | undefined, accountId: number) {
  const label = name?.trim();
  return label ? `${label}（#${accountId}）` : `账户 #${accountId}`;
}

function friendlyAccessError(message: string) {
  if (message === "Account access is required") {
    return "你尚未被分配为可进入人员。请先点击「分配人员」把自己加入后再进入。";
  }
  return message;
}

export default function AuthorizationsPage() {
  const { accountId: id } = useParams();
  const { account, refreshAccounts } = useAccountWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: AuthTab = searchParams.get("tab") === "received" ? "received" : "outbound";
  const assignParam = searchParams.get("assign");

  const [outbound, setOutbound] = useState<Authorization[]>([]);
  const [inbound, setInbound] = useState<Authorization[]>([]);
  const [canManageOutbound, setCanManageOutbound] = useState(false);
  const [canManageAssignees, setCanManageAssignees] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [enteringId, setEnteringId] = useState<number | null>(null);
  const [assignTarget, setAssignTarget] = useState<Authorization | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Authorization | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<AuthorizationFormValues>({
    resolver: zodResolver(authorizationSchema),
    defaultValues: { permission: "read_only", grantee_account_id: "" },
  });

  function setTab(next: AuthTab) {
    if (next === "received") {
      setSearchParams({ tab: "received" }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }

  function openAssignDialog(authorization: Authorization) {
    setAssignTarget(authorization);
    setSearchParams(
      { tab: "received", assign: String(authorization.id) },
      { replace: true },
    );
  }

  function closeAssignDialog() {
    setAssignTarget(null);
    setSearchParams({ tab: "received" }, { replace: true });
  }

  function loadOutbound() {
    if (!id) return;
    api
      .fetchForAccount<{ third_party_authorizations: Authorization[] }>(
        id,
        `/accounts/${id}/third_party_authorizations`,
      )
      .then(({ third_party_authorizations }) => setOutbound(third_party_authorizations))
      .catch((caughtError) =>
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载授权记录"),
      );
  }

  function loadInbound() {
    if (!id) return;
    api
      .fetch<{ third_party_authorizations: Authorization[] }>("/third_party_authorizations/inbound")
      .then(({ third_party_authorizations }) => {
        setInbound(
          third_party_authorizations.filter(
            (item) => String(item.grantee_account_id) === id && item.status === "active",
          ),
        );
      })
      .catch((caughtError) =>
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载收到的授权"),
      );
  }

  useEffect(() => {
    setError(null);
    if (tab === "outbound") loadOutbound();
    else loadInbound();
  }, [id, tab]);

  useEffect(() => {
    if (tab !== "received" || !assignParam || !canManageAssignees) return;
    const authId = Number(assignParam);
    if (!Number.isFinite(authId)) return;
    const found = inbound.find((item) => item.id === authId);
    if (found) setAssignTarget(found);
  }, [tab, assignParam, inbound, canManageAssignees]);

  useEffect(() => {
    if (!id || !user?.id) {
      setCanManageOutbound(false);
      setCanManageAssignees(false);
      return;
    }

    api
      .fetchForAccount<{ memberships: Membership[] }>(id, `/accounts/${id}/memberships`)
      .then(({ memberships }) => {
        const membership = memberships.find((item) => item.user_id === user.id);
        const admin = membership?.role === "owner" || membership?.role === "admin";
        setCanManageOutbound(Boolean(admin));
        setCanManageAssignees(Boolean(admin));
      })
      .catch(() => {
        setCanManageOutbound(false);
        setCanManageAssignees(false);
      });
  }, [id, user?.id]);

  async function onCreate(values: AuthorizationFormValues) {
    if (!id) return;
    clearErrors("root");
    try {
      const { third_party_authorization } = await api.fetchForAccount<{
        third_party_authorization: Authorization;
      }>(id, `/accounts/${id}/third_party_authorizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          grantee_account_id: Number(values.grantee_account_id),
          authorization_type: "service_provider",
        }),
      });
      setOutbound((current) => [...current, third_party_authorization]);
      reset({ permission: "read_only", grantee_account_id: "" });
      setCreateOpen(false);
      toast.success("授权已创建");
    } catch (caughtError) {
      setFormError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "创建授权失败，请稍后重试",
      });
    }
  }

  async function updatePermission(authorization: Authorization, permission: Authorization["permission"]) {
    if (authorization.permission === permission) return;
    setBusyId(authorization.id);
    setError(null);
    try {
      const { third_party_authorization } = await api.fetchForAccount<{
        third_party_authorization: Authorization;
      }>(id ?? "", `/third_party_authorizations/${authorization.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permission }),
      });
      setOutbound((current) =>
        current.map((item) => (item.id === authorization.id ? third_party_authorization : item)),
      );
      toast.success("权限已更新");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "更新授权失败，请稍后重试");
    } finally {
      setBusyId(null);
    }
  }

  async function confirmRevokeAuthorization() {
    if (!revokeTarget) return;
    const authorization = revokeTarget;
    setBusyId(authorization.id);
    setError(null);
    try {
      await api.fetchForAccount(id ?? "", `/third_party_authorizations/${authorization.id}`, {
        method: "DELETE",
      });
      setOutbound((current) =>
        current.map((item) => (item.id === authorization.id ? { ...item, status: "revoked" } : item)),
      );
      setRevokeTarget(null);
      toast.success("授权已撤销");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "撤销授权失败，请稍后重试");
    } finally {
      setBusyId(null);
    }
  }

  async function enterAccount(authorization: Authorization) {
    setEnteringId(authorization.id);
    setError(null);
    try {
      await api.fetch("/context", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: authorization.grantor_account_id }),
      });
      // 分配人员后列表可能刚变，进入前刷新，左上角才能立刻出现代管账户
      await refreshAccounts().catch(() => undefined);
      rememberAccountId(authorization.grantor_account_id);
      navigate(`/accounts/${authorization.grantor_account_id}`);
    } catch (caughtError) {
      const message =
        caughtError instanceof ApiError
          ? friendlyAccessError(caughtError.message)
          : "无法进入被授权账户";
      setError(message);
    } finally {
      setEnteringId(null);
    }
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="代管授权"
        description="管理本账户对外授权，以及收到的其他账户授权。"
        actions={
          tab === "outbound" && canManageOutbound ? (
            <Button
              type="button"
              onClick={() => {
                clearErrors();
                reset({ permission: "read_only", grantee_account_id: "" });
                setCreateOpen(true);
              }}
            >
              <Plus className="size-4" />
              新增授权
            </Button>
          ) : null
        }
      />

      <div role="tablist" aria-label="代管授权类型" className="flex gap-1 border-b border-border">
        {(
          [
            { key: "outbound", label: "对外授权" },
            { key: "received", label: "收到的授权" },
          ] as const
        ).map((item) => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.key)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {error && <AdminAlert>{error}</AdminAlert>}

      {tab === "outbound" ? (
        !canManageOutbound ? (
          <AdminAlert tone="info">仅账户所有者或管理员可管理对外授权。</AdminAlert>
        ) : outbound.length === 0 ? (
          <AdminEmptyState>暂无对外授权。点击右上角新增。</AdminEmptyState>
        ) : (
          <AdminList>
            {outbound.map((authorization) => {
              const active = authorization.status === "active";
              const granteeLabel = accountTitle(
                authorization.grantee_account_name,
                authorization.grantee_account_id,
              );
              return (
                <AdminListRow key={authorization.id}>
                  <div className="min-w-0 space-y-1">
                    <div className="font-medium">{granteeLabel}</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <AdminPill tone={active ? "success" : "muted"}>
                        {active ? "生效中" : "已撤销"}
                      </AdminPill>
                      <AdminPill>服务商类型</AdminPill>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={authorization.permission}
                      disabled={!active || busyId === authorization.id}
                      onValueChange={(value) =>
                        updatePermission(authorization, value as Authorization["permission"])
                      }
                    >
                      <SelectTrigger className="w-28" aria-label={`${granteeLabel} 的权限`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.entries(permissionLabels) as [Authorization["permission"], string][]).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!active || busyId === authorization.id}
                      onClick={() => setRevokeTarget(authorization)}
                    >
                      撤销
                    </Button>
                  </div>
                </AdminListRow>
              );
            })}
          </AdminList>
        )
      ) : inbound.length === 0 ? (
        <AdminEmptyState>「{account?.name ?? `账户 #${id}`}」暂未收到可用授权。</AdminEmptyState>
      ) : (
        <AdminList>
          {inbound.map((authorization) => {
            const grantorLabel = accountTitle(
              authorization.grantor_account_name,
              authorization.grantor_account_id,
            );
            return (
              <AdminListRow key={authorization.id}>
                <div className="min-w-0 space-y-1">
                  <div className="font-medium">{grantorLabel}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <AdminPill tone={authorization.permission === "maintain" ? "warning" : "muted"}>
                      {authorization.permission === "maintain" ? "代管权限" : "只读权限"}
                    </AdminPill>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canManageAssignees && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openAssignDialog(authorization)}
                    >
                      分配人员
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => enterAccount(authorization)}
                    disabled={enteringId === authorization.id}
                  >
                    {enteringId === authorization.id ? "进入中…" : "进入账户"}
                  </Button>
                </div>
              </AdminListRow>
            );
          })}
        </AdminList>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) clearErrors();
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>新增对外授权</DialogTitle>
            <DialogDescription>输入被授权账户 ID，并选择只读或代管权限。</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit(onCreate)} noValidate>
            <div className="space-y-2">
              <Label htmlFor="grantee-account-id">被授权账户 ID</Label>
              <Input
                id="grantee-account-id"
                type="number"
                {...register("grantee_account_id")}
                aria-invalid={Boolean(errors.grantee_account_id)}
              />
              {errors.grantee_account_id && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.grantee_account_id.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>权限</Label>
              <Controller
                name="permission"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger aria-invalid={Boolean(errors.permission)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(permissionLabels) as [Authorization["permission"], string][]).map(
                        ([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {errors.root && <AdminAlert>{errors.root.message}</AdminAlert>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "授权中…" : "创建"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {id && assignTarget ? (
        <AssignAssigneesDialog
          open
          onOpenChange={(open) => {
            if (!open) closeAssignDialog();
          }}
          granteeAccountId={id}
          authorizationId={assignTarget.id}
          grantorLabel={accountTitle(assignTarget.grantor_account_name, assignTarget.grantor_account_id)}
          onSaved={() => {
            void refreshAccounts();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={revokeTarget != null}
        onOpenChange={(open) => {
          if (!open && busyId == null) setRevokeTarget(null);
        }}
        title="撤销对外授权"
        description={
          revokeTarget
            ? `确定撤销对「${accountTitle(revokeTarget.grantee_account_name, revokeTarget.grantee_account_id)}」的授权吗？受派人将无法再进入该账户。`
            : ""
        }
        confirmLabel="撤销授权"
        tone="destructive"
        confirming={busyId != null && revokeTarget != null && busyId === revokeTarget.id}
        onConfirm={() => void confirmRevokeAuthorization()}
      />
    </div>
  );
}
